import { apiClient } from "../../base/ApiClient";

const BASE_URL = '/communications';

export const messagingService = {
  // ============ GROUPS ============
  getGroups: async () => {
    const response = await apiClient.get(`${BASE_URL}/messaging-groups/`);
    return response.data;
  },

  createGroup: async (data) => {
    const response = await apiClient.post(`${BASE_URL}/messaging-groups/`, data);
    return response.data;
  },

  getGroup: async (id) => {
    const response = await apiClient.get(`${BASE_URL}/messaging-groups/${id}/`);
    return response.data;
  },

  updateGroup: async (id, data) => {
    const response = await apiClient.patch(`${BASE_URL}/messaging-groups/${id}/`, data);
    return response.data;
  },

  deleteGroup: async (id) => {
    await apiClient.delete(`${BASE_URL}/messaging-groups/${id}/`);
  },

  addGroupMembers: async (id, user_ids) => {
    const response = await apiClient.post(`${BASE_URL}/messaging-groups/${id}/add_members/`, { user_ids });
    return response.data;
  },

  removeGroupMember: async (id, user_id) => {
    await apiClient.post(`${BASE_URL}/messaging-groups/${id}/remove_member/`, { user_id });
  },

  getGroupMembers: async (id) => {
    const response = await apiClient.get(`${BASE_URL}/messaging-groups/${id}/members/`);
    return response.data;
  },

  // ============ CONVERSATIONS ============
  getDefaultConversation: async () => {
    const response = await apiClient.get(`${BASE_URL}/conversations/default/`);
    return response.data;
  },

  getConversations: async (page = 1, pageSize = 10) => {
    const response = await apiClient.get(`${BASE_URL}/conversations/?page=${page}&page_size=${pageSize}`);
    return response.data;
  },

  createConversation: async (data) => {
    const response = await apiClient.post(`${BASE_URL}/conversations/`, data);
    return response.data;
  },

  getConversation: async (id) => {
    const response = await apiClient.get(`${BASE_URL}/conversations/${id}/`);
    return response.data;
  },

  getConversationMessages: async (id, limit = 50) => {
    const response = await apiClient.get(`${BASE_URL}/conversations/${id}/messages/?limit=${limit}`);
    return response.data;
  },

  // ============ MESSAGES ============
  markMessageRead: async (id) => {
    const response = await apiClient.post(`${BASE_URL}/messages/${id}/mark_read/`);
    return response.data;
  },

  getMessageReadStatus: async (id) => {
    const response = await apiClient.get(`${BASE_URL}/messages/${id}/read_status/`);
    return response.data;
  },

  deleteMessage: async (id) => {
    await apiClient.delete(`${BASE_URL}/messages/${id}/`);
  },

  editMessage: async (id, content) => {
    const response = await apiClient.patch(`${BASE_URL}/messages/${id}/`, { content });
    return response.data;
  },

  // ============ REACTIONS ============
  getMessageReactions: async (id) => {
    const response = await apiClient.get(`${BASE_URL}/messages/${id}/reactions/`);
    return response.data;
  },

  addReaction: async (id, reaction) => {
    const response = await apiClient.post(`${BASE_URL}/messages/${id}/reactions/`, { reaction });
    return response.data;
  },

  removeReaction: async (id, reaction) => {
    await apiClient.delete(`${BASE_URL}/messages/${id}/reactions/${reaction}/`);
  },

  // ============ ATTACHMENTS ============
  getMessageAttachments: async (id) => {
    const response = await apiClient.get(`${BASE_URL}/messages/${id}/attachments/`);
    return response.data;
  },

  uploadAttachment: async (messageId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${BASE_URL}/messages/${messageId}/attachments/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteAttachment: async (messageId, attachmentId) => {
    await apiClient.delete(`${BASE_URL}/messages/${messageId}/attachments/${attachmentId}/`);
  },

  // ============ STATS ============
  getUnreadCount: async () => {
    const response = await apiClient.get(`${BASE_URL}/messages/unread_count/`);
    return response.data;
  },

  markAllRead: async (conversationId) => {
    const response = await apiClient.post(`${BASE_URL}/conversations/${conversationId}/mark_all_read/`);
    return response.data;
  },

  getConversationStats: async (id) => {
    const response = await apiClient.get(`${BASE_URL}/conversations/${id}/stats/`);
    return response.data;
  },
};

// ============================================
// WEBSOCKET SERVICE - শুধু প্রয়োজনীয় মেথড
// ============================================
export class WebSocketService {
  constructor() {
    this.socket = null;
    this.listeners = {};
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.conversationId = null;
    this.token = null;
    this.isConnecting = false;
    this.heartbeatInterval = null;
    this.connectionTimeout = null;
  }

  connect(conversationId, token) {
    if (this.isConnecting || this.socket?.readyState === WebSocket.OPEN) {
      return;
    }

    this.disconnect();
    this.conversationId = conversationId;
    this.token = token;
    this.isConnecting = true;

    const wsUrl = process.env.REACT_APP_WS_URL || 'ws://localhost:8000';
    const url = `${wsUrl}/ws/chat/${conversationId}/?token=${token}`;

    try {
      this.socket = new WebSocket(url);

      this.connectionTimeout = setTimeout(() => {
        if (this.socket?.readyState !== WebSocket.OPEN) {
          this.emit('error', { error: 'Connection timeout' });
          this.socket?.close();
          this.isConnecting = false;
        }
      }, 10000);

      this.socket.onopen = this.handleOpen.bind(this);
      this.socket.onmessage = this.handleMessage.bind(this);
      this.socket.onclose = this.handleClose.bind(this);
      this.socket.onerror = this.handleError.bind(this);
    } catch (error) {
      this.isConnecting = false;
      this.emit('error', { error: 'Failed to connect', details: error });
    }
  }

  handleOpen() {
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000;
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
    this.emit('connected', { conversationId: this.conversationId });
    this.startHeartbeat();
  }

  handleMessage(event) {
    try {
      const data = JSON.parse(event.data);
      this.emit(data.type, data.data || data);
    } catch (error) {
      this.emit('error', { error: 'Failed to parse message', details: error });
    }
  }

  handleClose() {
    this.isConnecting = false;
    this.stopHeartbeat();
    this.emit('disconnected');
    this.reconnect();
  }

  handleError(error) {
    this.isConnecting = false;
    this.emit('error', { error: 'WebSocket error', details: error });
  }

  reconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.emit('reconnect_failed');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts), 30000);

    setTimeout(() => {
      if (this.conversationId && this.token) {
        this.connect(this.conversationId, this.token);
      }
    }, delay);
  }

  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.send({ type: 'ping' });
      }
    }, 30000);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  disconnect() {
    this.stopHeartbeat();
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.conversationId = null;
    this.token = null;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
  }

  send(data) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(data));
      return true;
    }
    return false;
  }

  sendMessage(content) {
    return this.send({
      type: 'message',
      content: content,
      direction: 'OUTBOUND',
    });
  }

  sendTyping(isTyping) {
    return this.send({
      type: 'typing',
      is_typing: isTyping,
    });
  }

  sendReadReceipt(messageId) {
    return this.send({
      type: 'read_receipt',
      message_id: messageId,
    });
  }

  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }

  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in listener for ${event}:`, error);
        }
      });
    }
  }

  isConnected() {
    return this.socket?.readyState === WebSocket.OPEN;
  }
}

export const wsService = new WebSocketService();

export const QK = {
  conversations: 'conversations',
  messages: 'messages',
  groupMembers: 'groupMembers',
  unreadCount: 'unreadCount',
  conversationStats: 'conversationStats',
  messageReactions: 'messageReactions',
  messageAttachments: 'messageAttachments',
};